import { DropdownMenuOption } from '@/components/core-components/types';
import { DROPDOWN_TIMEZONES } from '@/lib/constants/timezones';
import { getLocalTimezone } from '@/lib';

interface TimezoneData {
    value: string;
    abbr: string;
    offset: number;
    isdst: boolean;
    text: string;
    principal_cities: string[];
    utc: string[];
}

export interface TimezoneOption extends DropdownMenuOption {
    zones: string[];
    offset: number;
    abbr: string;
    showName: string;
}

// Extract name from timezone text field (e.g., "(UTC-12:00) International Date Line West" -> "International Date Line West")
const getNameFromText = (str: string): string => {
    const regex = /\)([A-Za-z\s,.-]+)[+(]*/;
    const match = str.match(regex);
    return match && match[1] ? match[1].trim() : '';
};

// Helper function to get display name from principal cities or UTC zones
// Matches Vue implementation in BaseDefaults.js getTimeZonesRawList()
const getDisplayName = (timezone: TimezoneData): string => {
    if (timezone.principal_cities && timezone.principal_cities.length > 0) {
        return timezone.principal_cities.join(', ');
    }

    const texts: string[] = [];
    const utcZones = timezone.utc || [];
    for (const utcZone of utcZones) {
        const parts = utcZone.split('/');
        if (parts.length === 2 && parts[0] && parts[1] && parts[0].toLowerCase() !== 'etc') {
            texts.push(parts[1].replace(/_/g, ' '));
        }
    }

    let showName = '';
    if (texts.length > 0 && texts.length <= 4) {
        showName = texts.join(', ');
    } else if (texts.length > 4) {
        showName = texts.slice(0, 3).join(', ');
    }

    // Fallback to extracting name from text field (matches Vue behavior)
    if (showName === '') {
        showName = getNameFromText(timezone.text);
    }

    return showName;
};

// Format: "EST - New York, Toronto, Havana | UTC -5"
// Matches Vue implementation - skips timezones with empty utc arrays
const buildTimezoneOptions = (): TimezoneOption[] => {
    const options: TimezoneOption[] = [];

    for (let i = 0; i < DROPDOWN_TIMEZONES.length; i++) {
        const tz = DROPDOWN_TIMEZONES[i] as TimezoneData;

        // Skip timezones with no UTC zones (matches Vue behavior)
        if (!tz.utc || tz.utc.length === 0) {
            continue;
        }

        const displayName = getDisplayName(tz);
        const offsetSign = tz.offset > 0 ? '+' : '';
        const offset = `${offsetSign}${tz.offset}`;

        options.push({
            label: `${tz.abbr} - ${displayName} | UTC ${offset}`,
            value: tz.utc[0] as string,
            zones: tz.utc,
            offset: tz.offset,
            abbr: tz.abbr,
            showName: displayName,
        });
    }

    return options;
};

export const TIMEZONE_OPTIONS = buildTimezoneOptions();

// Find timezone option by matching any of its zones
export const findTimezoneOption = (timezoneName: string): TimezoneOption | undefined => {
    return TIMEZONE_OPTIONS.find(option => option.zones.some(zone => zone === timezoneName));
};

// Get the default timezone option (user's current timezone or first option)
export const getDefaultTimezoneOption = (): TimezoneOption => {
    const userTimezone = getLocalTimezone();
    const matchedOption = findTimezoneOption(userTimezone);

    if (matchedOption) {
        return matchedOption;
    }

    return TIMEZONE_OPTIONS[0] as TimezoneOption;
};
